import { useRealtimeStore } from "@/store/realtime.store";
import { cn } from "@roman/shared/utils";
import type React from "react";
import { useMemo } from "react";

interface LiveBadgeProps {
  /** If displayed within a tab */
  isInTab?: boolean;

  /** If the tab is selected */
  isSelectedTab?: boolean;

  /** If displayed within a menu (footer navigation) */
  isInMenu?: boolean;

  /** Custom class names */
  className?: string;
  subClassName?: string;
  iconClassName?: string;
  textClassName?: string;

  /**
   * Display text (default: "LIVE")
   * Can be changed to something like "LIVE >>" for LiveDetailButton
   */
  text?: string;
}

// LiveBadge component: no user, or no live round (isInMenu = true) -> return null
export const LiveBadge: React.FC<LiveBadgeProps> = ({
  isInTab = false,
  isSelectedTab = false,
  isInMenu = false,
  className = "",
  subClassName = "",
  iconClassName = "",
  textClassName = "",
  text = "LIVE",
}) => {
  const hasLiveRounds = useRealtimeStore((state) => state.hasLiveRounds);

  const { wrapperClass, textFinal, iconFinal } = useMemo(() => {
    let wClass = "";
    let iClass = "";
    let tFinal = "";

    if (!hasLiveRounds && !isInMenu) {
      wClass =
        "rounded-full px-2 py-[2px] flex justify-center items-center space-x-1 text-light-gray border border-light-gray";
      iClass = cn("relative flex h-[7px] w-[7px] items-center justify-center", iconClassName);
      tFinal = cn("text-light-gray font-semibold text-[10px] leading-[10px]", textClassName);
      if (isSelectedTab) {
        wClass = cn(wClass, "bg-primary");
      } else if (isInTab) {
        wClass = cn(wClass, "bg-white");
      }
      return {
        wrapperClass: cn(wClass, subClassName),
        textFinal: tFinal,
        iconFinal: iClass,
      };
    }

    if (!hasLiveRounds && isInMenu) {
      return {
        wrapperClass: "hidden",
        textFinal: "",
        iconFinal: "",
      };
    }

    wClass = "rounded-full px-2 py-[2px] flex justify-center items-center bg-primary space-x-1";
    iClass = cn("relative flex h-[7px] w-[7px] items-center justify-center", iconClassName);
    tFinal = cn("text-white font-semibold text-[10px] leading-[10px]", textClassName);

    if (isInTab && isSelectedTab) {
      wClass = cn(wClass, "border border-white");
      tFinal = cn(tFinal, "!text-[12px]");
    } else if (isInTab) {
      wClass = cn(
        "rounded-full px-2 py-[2px] flex justify-center items-center bg-white text-primary space-x-1 border border-primary",
      );
      iClass = cn("relative flex h-[7px] w-[7px]", iconClassName);
      tFinal = cn("font-semibold text-[10px]", textClassName, "text-primary");
    }
    if (!(isInTab || isInMenu)) {
      // when in the entry card
      wClass = cn(wClass, "px-2");
    }

    return {
      wrapperClass: cn(wClass, subClassName),
      textFinal: tFinal,
      iconFinal: iClass,
    };
  }, [isInTab, hasLiveRounds, isSelectedTab, iconClassName, textClassName, subClassName, isInMenu]);

  return (
    <div className={cn("flex", className)}>
      {/* Wrapper */}
      <div className={wrapperClass}>
        {/* Animated round icon */}
        <span className={iconFinal}>
          {hasLiveRounds ? (
            <>
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#01DCF9] opacity-75" />
              <span className="border border-none relative inline-flex rounded-full h-[7px] w-[7px] bg-[#01DCF9]" />
            </>
          ) : (
            <>
              <span className="border border-none rounded-full bg-light-gray text-light-gray text-[8px]">●</span>
            </>
          )}
        </span>

        {/* Text */}
        <span className={textFinal}>{isInTab || isInMenu ? text : `${text} >>`}</span>
      </div>
    </div>
  );
};
